<template>
  <layout-report :renderBackAndForthFooter="false" :isProgressDone="true" :showFooter="false">
    <v-container class="padded">
      <v-row>
        <v-col cols="12">
          <v-card flat>
            <v-card-title class="text-h4 px-0">
              {{ $t(title) }}
              <v-spacer></v-spacer>
              <v-btn
                v-for="(action, index) in actions"
                :key="index"
                elevation="0"
                small
                class="ml-2 d-none d-sm-block"
                :color="partnerColor.primary"
                :outlined="action.isOutlined"
                dark
                @click="action.click"
              >
                {{ $t(action.title) }}
              </v-btn>
            </v-card-title>
            <v-card-subtitle v-html="$t(description)" class="pl-0"></v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>
      <notifications />
      <v-row dense v-for="(nav, i) in navItems" :key="i" class="pb-8">
        <template v-if="nav.isEnabled">
          <v-col cols="12" class="pb-4">
            <p class="mb-0 h3">{{ $t(nav.title) }}</p>
            <v-divider></v-divider>
          </v-col>
          <v-col cols="6" md="4" v-for="(item, j) in nav.items" :key="j">
            <v-card @click="item.click" v-if="item.isEnabled">
              <v-img
                class="white--text align-end"
                height="150px"
                gradient="to top right, rgba(30, 30, 30, 0.58), rgba(30, 30, 30, 0.58)"
                :src="item.src"
              >
                <v-card-subtitle>{{ $t(item.title) }}</v-card-subtitle>
              </v-img>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </v-container>
  </layout-report>
</template>

<script lang="ts">
import Notifications from "@/components/thg/Notifications.vue";
import Debug from "@/components/utility/Debug.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import { PartnerColor } from "@/lib/partnerColor";
import { PartnerTypeEnum } from "@/store/enum/partner/partner.type.enum";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { UserModule } from "@/store/modules/me-user.store";
import { NotificationModule } from "@/store/modules/notification.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Vue } from "vue-property-decorator";

@Component({
  components: {
    Notifications,
    LayoutReport,
    Debug
  }
})
export default class ThgOverview extends Vue {
  isLoading = false;

  get partnerColor() {
    return new PartnerColor();
  }

  get actions() {
    return [
      {
        click: this.sendMail,
        title: "report.thgOverview.contact",
        icon: "mdi-email",
        isOutlined: true,
        isEnabled: true
      },
      {
        click: this.toRating,
        title: "report.thgOverview.review",
        icon: "mdi-star",
        isOutlined: false,
        isEnabled: true
      }
    ].filter((x) => x.isEnabled);
  }

  get navItems() {
    return [
      {
        title: "report.thgVehicleOverview.title",
        isEnabled: true,
        items: [
          {
            title: "report.thgOverview.vehicle.new",
            click: this.toNewCar,
            src: "https://static.mmmint.ai/wirkaufendeinethg/thg-overview-new-vehicle-1.jpeg",
            isEnabled: true
          },
          {
            title: "report.thgOverview.vehicle.overview",
            click: this.toVehicleOverview,
            src: "https://static.mmmint.ai/wirkaufendeinethg/parkplatz-1.jpeg",
            isEnabled: true
          }
        ].filter((x) => x.isEnabled)
      },
      {
        title: "report.thgChargingStationOverview.title",
        isEnabled: this.isChargingStationEnabled,
        items: [
          {
            title: "report.thgOverview.chargingStation.overview",
            click: this.toChargingStationOverview,
            src: "https://static.mmmint.ai/wirkaufendeinethg/charging-station-1.jpeg",
            isEnabled: true
          }
        ].filter((x) => x.isEnabled)
      },
      {
        title: "report.thgOverview.other",
        isEnabled: true,
        items: [
          {
            title: "report.thgOverview.marketplace.insuranceCheck.title",
            click: this.toInsuranceCheck,
            src: "https://static.mmmint.ai/wirkaufendeinethg/insurance-check-1.jpeg",
            isEnabled: this.isMarketplaceEnabled
          },
          {
            title: "report.thgOverview.billing",
            click: this.toBillings,
            src: "https://static.mmmint.ai/wirkaufendeinethg/thg-overview-contact-cash-1.jpeg",
            isEnabled: true
          },
          {
            title: "report.thgOverview.editProfile",
            click: this.toProfile,
            src: "https://static.mmmint.ai/wirkaufendeinethg/thg-overview-contact-data-1.jpeg",
            isEnabled: true
          },
          {
            title: "report.thgOverview.invite",
            click: this.toInviteFriends,
            src: "https://static.mmmint.ai/wirkaufendeinethg/thg-overview-invite-friends-1.jpeg",
            isEnabled: this.isInviteFriendsEnabled
          }
        ].filter((x) => x.isEnabled)
      }
    ].filter((x) => x.isEnabled);
  }

  get isMintFuture(): boolean {
    const isMintFuture = PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_MINT_FUTURE;

    return isMintFuture;
  }

  get isPartnerMarketplace(): boolean {
    return PartnerModule?.partner?.settings?.bookedServices.marketplace || false;
  }

  get isChargingStationEnabled(): boolean {
    if (!PartnerModule.partner.settings) {
      return false;
    }

    if (!PartnerModule.partner.settings.bookedServices["chargingStation"]) {
      return false;
    }

    return PartnerModule.partner.settings.bookedServices.chargingStation;
  }

  get isMarketplaceEnabled(): boolean {
    return this.isPartnerMarketplace && FeatureModule.isMarketplaceEnabled.isInsuranceCheckEnabled;
  }

  closeNotification(index: number) {
    NotificationModule.popNotification(index);
  }

  get isAuthenticated(): boolean {
    return UserModule.isAuthenticated;
  }

  get title() {
    return `${this.$t("report.thgOverview.title")}  ${UserModule.user.firstName} ☀️`;
  }

  get description() {
    return this.$t("report.thgOverview.description");
  }

  get isInviteFriendsEnabled() {
    return FeatureModule.isInviteFriendsEnabled;
  }

  toNewCar() {
    this.$router.push({ path: "/thghome" });
  }

  toNewChargingStation() {
    this.$router.push({ path: "/charging-station/new" });
  }

  toVehicleOverview() {
    this.$router.push({ name: "ThgVehicleOverview" });
  }

  toChargingStationOverview() {
    this.$router.push({ name: "ThgChargingStationOverview" });
  }

  toProfile() {
    this.$router.push({ name: "Profile" });
  }

  toInviteFriends() {
    this.$router.push({ name: "ThgInviteFriends" });
  }

  toBillings() {
    this.$router.push({ name: "ThgBillingOverview" });
  }

  toInsuranceCheck() {
    this.$router.push({ name: "ThgInsuranceCheck" });
  }

  toRating() {
    this.$router.push({ name: "Rating" });
  }

  sendMail() {
    const mailText = `mailto:${ConfigModule.supportMail}`;

    window.open(mailText, "_blank");
  }
}
</script>

<style scoped>
.contain {
  width: 100%;
  padding: 1em;
}

.v-alert {
  cursor: pointer;
}
</style>
